import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  useDisclosure,
} from "@nextui-org/react";

// icons
import { VerticalDotsIcon } from "../../../assets/icons/VerticalDotsIcon";
import ViewsIcon from "../../../assets/icons/ViewsIcon";
import HeartIcon from "../../../assets/icons/Hearticon";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { sharedItem } from "src/types/api/Share";
import SharedFolderIcon from "../../../assets/icons/SharedFolderIcon";
import { create } from "zustand";
import { useToggleFolderFavorite } from "src/hooks/useFavoriteHooks";
import FolderDetailsModal from "src/components/modals/FolderDetailsModal";
import { useListAllDirectories } from "src/hooks/useListAllDirectories";
import { Item } from 'src/types/api/ListFolderFiles';
import useListSharedWithFolderContents from "src/hooks/useListSharedWithFolderContents";
import ShareFolderModal from "src/components/modals/ShareFolderModal";
import DeleteFolderModal from "src/components/modals/DeleteFolderModal";
import EditFolderModal from "src/components/modals/EditFolderModal";
import { useSharedWithFolderStore } from "./SharedWithMeFolderCard";
import useFolderStore from 'src/store/api/useFolderStore';
import { useAuthStore } from "src/store/api/useAuthStore";
import { useClubStore } from "src/store/api/useClubStore";

// interface FolderState {
//   folderId: string | null;
//   directoryId: string | null;
//   folderPermissions: string | null;
//   setFolderId: (id: string) => void;
//   setDirectoryId: (id: string) => void;
//   setFolderPermissions: (permissions: string) => void;
// }

// export const useSharedWithFolderStore = create<FolderState>((set) => ({
//   folderId: null,
//   directoryId: null,
//   folderPermissions: null,
//   setFolderId: (id) => {
//     set({ folderId: id });
//   },
//   setDirectoryId: (id) => {
//     set({ directoryId: id });
//   },
//   setFolderPermissions: (permissions) => {
//     set({ folderPermissions: permissions });
//   },
// }));

interface SharedWithMeFolderCardProps {
  folder: Item;
  permissions: string;
}

const SharedWithMeFolderCard3: React.FC<SharedWithMeFolderCardProps> = ({
  folder, permissions,
}) => {
  const [liked, setLiked] = useState(Number(folder.is_favorite) === 1);
  const { t } = useTranslation();
  const { setFolderId, setDirectoryId, setFolderPermissions } = useSharedWithFolderStore();
  const toggleFolderFavorite = useToggleFolderFavorite();
  const { directories } = useListAllDirectories();
  const [, setSelectedFolderId] = useState<string | null>(null);
  const { pushFolder } = useFolderStore();

  useDisclosure();
  const { isOpen: isFolderDetailsOpen, onOpen: onOpenFolderDetails, onClose: onCloseFolderDetails } = useDisclosure();
  const { isOpen: isEditFolderOpen, onOpen: onOpenEditFolder, onClose: onCloseEditFolder } = useDisclosure();
  const { isOpen: isDeleteFolderOpen, onOpen: onOpenDeleteFolder, onClose: onCloseDeleteFolder } = useDisclosure();
  const { isOpen: isShareFolderOpen, onOpen: onOpenShareFolder, onClose: onCloseShareFolder } = useDisclosure();


  useEffect(() => {
    setLiked(Number(folder.is_favorite) === 1);
  }, [folder.is_favorite]);



  const handleToggleLiked = async () => {
    try {
      setLiked((prev) => !prev); // Optimistic UI update
      await toggleFolderFavorite(folder.folder_id || ""); // API call to toggle favorite status
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
      setLiked((prev) => !prev); // Revert UI if the API call fails
    }
  };
  const handleView = () => {
    onOpenFolderDetails();
  };

  const handleEdit = (folderId: string) => {
    setSelectedFolderId(folderId);
    onOpenEditFolder();
  };

  const handleDelete = () => {
    setFolderId(folder.folder_id || '');
    onOpenDeleteFolder();
  };

  const handleShare = () => {
    onOpenShareFolder();
  };

  const handleSave = (names: string[], roles: string[]) => {
    // Add your save logic here
  };

  const handleCopyLink = () => {
    // Add your copy link logic here
  };

  // Extract year from created_at
  // const year = new Date(folder.created_at).getFullYear();

  // Find the directory with the matching directory_id
  const directory = directories?.find(
    (dir) => dir.directory_id === folder.directory_id
  );

  // Current year
  const currentYear = new Date().getFullYear();

  // Extract year from directory name
  const directoryYear = directory?.name?.match(/\d{4}/)?.[0] ?? null;
  const isCurrentYear = directoryYear === currentYear.toString();
  const { setTheSelectedFolderId } = useListSharedWithFolderContents();


  const isSaff = useAuthStore((state) => state.isSaff);
  const isClub = useAuthStore((state) => state.isClub);

  const userIsSaff = isSaff && !isClub;


  const dropdownItems = [
    <DropdownItem key="view-details" onClick={handleView}>
      {t("ViewDetails")}
    </DropdownItem>,
    userIsSaff && (
      <DropdownItem key="edit" onClick={() => handleEdit(folder.folder_id || '')}>
        {t("EditFolder")}
      </DropdownItem>
    ),
    userIsSaff && (
      <DropdownItem key="share" onClick={handleShare}>
        {t("Share")}
      </DropdownItem>
    ),
    userIsSaff && (
      <DropdownItem key="delete" color="danger" onClick={handleDelete}>
        {t("Delete")}
      </DropdownItem>
    ),
  ].filter(
    (item): item is React.ReactElement => Boolean(item)
  );

  return (
    <Card className="lg:w-[15%] w-[20rem] hover:scale-105" key={folder.folder_id}>
      <CardHeader className="justify-between">
        <Dropdown>
          <DropdownTrigger>
            <Button isIconOnly radius="full" size="sm" variant="light">
              <VerticalDotsIcon className="text-default-400" />
            </Button>
          </DropdownTrigger>
          <DropdownMenu>
            {dropdownItems}
          </DropdownMenu>
        </Dropdown>

        <Button
          isIconOnly
          className="text-default-900/60 data-[hover]:bg-foreground/10 -translate-y-2 translate-x-2"
          radius="full"
          variant="light"
          onPress={handleToggleLiked}
        >
          <HeartIcon
            className={liked ? "[&>path]:stroke-transparent" : ""}
            fill={liked ? "red" : "none"}
          />
        </Button>
      </CardHeader>

      <Link
        to="/sharedWithFileList"
        onClick={() => {
          setFolderId(folder.folder_id || "");
          setDirectoryId(folder.directory_id || "");
          setFolderPermissions(permissions);
          setTheSelectedFolderId(folder.folder_id || "");
          pushFolder(folder.folder_id || "", folder.name || "");

        }}
      >
        <CardBody className="px-3 py-0 text-small text-center text-default-400">
          <div className="flex justify-center items-center">
            <SharedFolderIcon height={70} width={70} />
          </div>
          <span className="pt-2 capitalize text-gray-900 text-lg font-bold">
            {folder.name && folder.name.length >= 40
              ? `${folder.name.slice(0, 40)}.....`
              : folder.name || ""}
          </span>
          <span
            className={`pt-2 capitalize ${isCurrentYear ? "text-success" : ""}`}
          >
            {directory ? directory.name : folder.directory_id}
          </span>
        </CardBody>

        <CardFooter className="gap-3 w-full">
          <div className="flex justify-between w-full text-gray-400">
            <div className="pt-2 capitalize">
              {/* <div className="flex justify-center items-start">
                <DownloadIcon color="rgb(156 163 175 / var(--tw-text-opacity))" />
                <span>{folder.download_count || 0}</span>
              </div> */}
            </div>
            <div className="pt-2 capitalize">
              <div className="flex justify-center items-start">
                <ViewsIcon
                  height="20"
                  color="rgb(156 163 175 / var(--tw-text-opacity))"
                />
                <span>{folder.view_count || 0}</span>
              </div>
            </div>
          </div>
        </CardFooter>
      </Link>

      <FolderDetailsModal isOpen={isFolderDetailsOpen} onClose={onCloseFolderDetails} folderDetails={folder || ''} />
      <EditFolderModal folderId={folder.folder_id || ''} folderName={folder.name || ''} isOpen={isEditFolderOpen} onClose={onCloseEditFolder} />
      <DeleteFolderModal isOpen={isDeleteFolderOpen} onClose={onCloseDeleteFolder} folderId={folder.folder_id || ''} folderType="sharedWithFolder" />
      <ShareFolderModal onSave={handleSave} onCopyLink={handleCopyLink} isOpen={isShareFolderOpen} onClose={onCloseShareFolder} resourceId={folder.folder_id || ''} resourceName={folder.name || ''} resourceType="folder" />
    </Card>
  );
};

export default SharedWithMeFolderCard3;
